import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Flame, Snowflake, ThermometerSun, RefreshCw, Filter, User2, Target, Clock, X } from "lucide-react";
import { api, formatApiErrorDetail } from "../api";
import { PageHeader, Badge, Btn } from "../components/common";
import AccountDrawer from "../components/AccountDrawer";
import { overlayCloseHandlers } from "../lib/overlayClose";
import { useAutoRefresh } from "../lib/autoRefresh";

const CICLO_OPTS = [
  { value: "", label: "Todos" },
  { value: "lead", label: "Lead (cold)" },
  { value: "mql", label: "MQL (marketing)" },
  { value: "sql", label: "SQL (vendas)" },
  { value: "opportunity", label: "Oportunidade" },
];

const RATING_META = {
  hot: { label: "HOT", cls: "bg-red-100 text-red-700 border-red-200", Icon: Flame },
  warm: { label: "WARM", cls: "bg-amber-100 text-amber-700 border-amber-200", Icon: ThermometerSun },
  cold: { label: "COLD", cls: "bg-sky-100 text-sky-700 border-sky-200", Icon: Snowflake },
};

function ScoreBar({ value }) {
  const v = Math.max(0, Math.min(100, value || 0));
  const cls = v >= 70 ? "bg-red-500" : v >= 40 ? "bg-amber-500" : "bg-sky-500";
  return (
    <div className="w-full h-1.5 bg-slate-200 rounded-full overflow-hidden">
      <div className={`h-full ${cls}`} style={{ width: `${v}%` }} />
    </div>
  );
}

function BreakdownPill({ label, value, max }) {
  const pct = Math.round((value / max) * 100);
  return (
    <div className="text-[10px]" title={`${value}/${max} (${pct}%)`}>
      <div className="text-slate-500 mb-0.5">{label}</div>
      <div className="font-mono font-semibold text-navy">{value}<span className="text-slate-400">/{max}</span></div>
    </div>
  );
}

export default function LeadQualification() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ciclo, setCiclo] = useState("");
  const [tab, setTab] = useState("ativas"); // "ativas" | "arquivadas"
  const [arquivadasCount, setArquivadasCount] = useState(0);
  const [drawerId, setDrawerId] = useState(null);
  const [showTiming, setShowTiming] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (ciclo) params.ciclo_vida = ciclo;
      if (tab === "arquivadas") params.arquivadas = "1";
      const { data } = await api.get("/crm/qualify", { params });
      setRows(data || []);
      if (tab === "arquivadas") setArquivadasCount((data || []).length);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [ciclo, tab]);
  useAutoRefresh(load); // eslint-disable-line
  useEffect(() => {
    api.get("/crm/qualify", { params: { limit: 200, arquivadas: "1" } })
      .then(({ data }) => setArquivadasCount((data || []).length))
      .catch(() => {});
  }, []);

  const recalcular = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/crm/qualify/recompute-all");
      toast.success(`Recalculado: ${data.n} contas.`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const stats = {
    hot: rows.filter((r) => r.rating === "hot").length,
    warm: rows.filter((r) => r.rating === "warm").length,
    cold: rows.filter((r) => r.rating === "cold").length,
    total: rows.length,
    avg: rows.length ? Math.round(rows.reduce((s, r) => s + (r.lead_score || 0), 0) / rows.length) : 0,
  };

  return (
    <>
      <PageHeader
        overline="CRM"
        title="Qualificacao de Leads"
        action={
          <div className="flex gap-2">
            <Btn variant="ghost" onClick={() => setShowTiming(true)} testid="qualify-timing-btn">
              Mapa de timing
            </Btn>
            <Btn variant="ghost" onClick={() => (window.location.href = "/app/crm/qualificacao/kanban")} testid="qualify-kanban-btn">
              Kanban
            </Btn>
            <Btn onClick={recalcular} testid="qualify-recompute" variant="ghost">
              <RefreshCw size={14} /> Recalcular tudo
            </Btn>
          </div>
        }
      />

      <div className="flex items-center gap-2 mb-4" data-testid="qualify-tabs">
        <button type="button" onClick={() => setTab("ativas")} data-testid="qualify-tab-ativas"
          className={`px-3 py-1.5 rounded-full border text-xs font-semibold transition-colors ${tab === "ativas" ? "bg-navy text-white border-navy" : "bg-white text-navy border-tint hover:border-navy"}`}>
          Activas
        </button>
        <button type="button" onClick={() => setTab("arquivadas")} data-testid="qualify-tab-arquivadas"
          className={`px-3 py-1.5 rounded-full border text-xs font-semibold transition-colors ${tab === "arquivadas" ? "bg-navy text-white border-navy" : "bg-white text-navy border-tint hover:border-navy"}`}>
          Arquivadas {arquivadasCount > 0 && `(${arquivadasCount})`}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4" data-testid="qualify-stats">
        {[
          ["Total", stats.total, "text-navy"],
          ["Score medio", stats.avg, "text-navy"],
          ["Hot", stats.hot, "text-red-600"],
          ["Warm", stats.warm, "text-amber-600"],
          ["Cold", stats.cold, "text-sky-600"],
        ].map(([k, v, cls]) => (
          <div key={k} className="border border-tint rounded-lg p-3 bg-white">
            <div className="text-[10px] uppercase tracking-wider text-muted2">{k}</div>
            <div className={`text-2xl font-bold ${cls}`}>{v}</div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3 mb-4 bg-white border border-tint rounded-lg px-3 py-2">
        <Filter size={14} className="text-slate-400" />
        <span className="text-xs text-muted2">Ciclo de vida:</span>
        <select value={ciclo} onChange={(e) => setCiclo(e.target.value)}
          data-testid="qualify-filter-ciclo"
          className="text-xs border border-tint rounded px-2 py-1">
          {CICLO_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <div className="flex-1" />
        <div className="text-[11px] text-slate-500">
          Formula: fit + completude + engagement + origem + recencia
        </div>
      </div>

      {loading && <div className="text-center text-muted2 py-8">A calcular...</div>}

      {!loading && rows.length === 0 && (
        <div className="text-center text-muted2 border border-tint rounded-lg py-12">
          {tab === "arquivadas" ? "Sem leads arquivadas." : "Sem contas para este filtro."}
        </div>
      )}

      <div className="space-y-2" data-testid="qualify-list">
        {rows.map((r, idx) => {
          const meta = RATING_META[r.rating || "cold"];
          const bk = r.score_breakdown || {};
          return (
            <button
              key={r.id}
              onClick={() => setDrawerId(r.id)}
              data-testid={`qualify-row-${r.id}`}
              className="w-full text-left flex items-center gap-4 border border-tint rounded-lg px-3 py-2.5 bg-white hover:border-blue hover:shadow-sm transition"
            >
              <div className="w-8 text-center font-mono text-xs font-bold text-slate-400">#{idx + 1}</div>
              <div className="w-16 shrink-0">
                <div className="text-2xl font-bold text-navy tabular-nums">{r.lead_score ?? 0}</div>
                <ScoreBar value={r.lead_score} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-navy truncate flex items-center gap-2">
                  {r.nome}
                  {r.codigo && <span className="text-[10px] font-mono text-blue">{r.codigo}</span>}
                </div>
                <div className="text-[11px] text-slate-500 flex items-center gap-2 mt-0.5">
                  <span>{r.setor || "Setor n/d"}</span>
                  <span>-</span>
                  <span>{r.dimensao || "dimensao n/d"}</span>
                  <span>-</span>
                  <span>origem: {r.origem || "-"}</span>
                  {tab === "arquivadas" && r.arquivo_motivo && (
                    <span className="text-amber-600">- motivo: {r.arquivo_motivo}</span>
                  )}
                </div>
              </div>
              <div className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded border ${meta.cls}`}>
                <meta.Icon size={11} /> {meta.label}
              </div>
              <div className="hidden lg:flex items-center gap-3 pl-2 border-l border-tint">
                <BreakdownPill label="Fit" value={bk.fit || 0} max={30} />
                <BreakdownPill label="Dados" value={bk.completude || 0} max={20} />
                <BreakdownPill label="Interac" value={bk.engagement || 0} max={30} />
                <BreakdownPill label="Origem" value={bk.origem || 0} max={10} />
                <BreakdownPill label="Recente" value={bk.recencia || 0} max={10} />
              </div>
              <Badge value={r.ciclo_vida || "lead"} />
            </button>
          );
        })}
      </div>

      <AccountDrawer accountId={drawerId} open={!!drawerId} onClose={() => { setDrawerId(null); load(); }} />

      {showTiming && (
        <TimingMapModal onClose={() => setShowTiming(false)} onOpenAccount={(id) => { setShowTiming(false); setDrawerId(id); }} />
      )}
    </>
  );
}

function TimingMapModal({ onClose, onOpenAccount }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/crm/qualify/timing-map");
      setData(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" data-testid="timing-map-modal" {...overlayCloseHandlers(onClose)}>
      <div className="bg-white rounded-xl w-full max-w-6xl mt-6 mb-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-tint">
          <div className="flex items-center gap-2">
            <Clock size={18} className="text-blue" />
            <h3 className="text-navy font-bold text-base">Mapa de timing de leads</h3>
          </div>
          <button onClick={onClose} data-testid="timing-map-close" className="text-slate-500 hover:text-navy"><X size={18} /></button>
        </div>
        <div className="p-5">
          <p className="text-[12px] text-muted2 mb-4">
            Leads agrupadas pelo campo BANT-timing. Clique numa lead para abrir os detalhes.
            Configure alertas e automatismos em <a href="/app/definicoes" className="text-blue underline">Definicoes &gt; Leads &gt; Automatismos</a>.
          </p>
          {loading && <div className="text-sm text-muted2">A carregar...</div>}
          {data && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {data.buckets.map((b) => (
                <div key={b.timing} className="border border-tint rounded-lg overflow-hidden" data-testid={`timing-bucket-${b.timing}`}>
                  <div className="bg-slate-50 border-b border-tint px-3 py-2 flex items-center justify-between">
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Timing</div>
                      <div className="text-sm font-bold text-navy">{b.timing}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-blue">{b.count}</div>
                      {b.rule?.enabled === false && <div className="text-[10px] text-slate-400">Aviso off</div>}
                      {b.rule?.enabled && <div className="text-[10px] text-slate-500">Aviso {b.rule.dias_antes_fim}d antes</div>}
                    </div>
                  </div>
                  <div className="max-h-64 overflow-y-auto divide-y divide-tint">
                    {(b.rows || []).length === 0 && <div className="text-xs text-muted2 py-6 text-center">Sem leads.</div>}
                    {(b.rows || []).map((r) => (
                      <button key={r.id}
                        onClick={() => onOpenAccount(r.id)}
                        className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2"
                        data-testid={`timing-row-${r.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-navy truncate">{r.nome}</div>
                          <div className="text-[10px] text-slate-500 truncate">{r.rating || "-"} - score {r.lead_score ?? 0} - {r.ciclo_vida || "lead"}</div>
                        </div>
                        {r.dias_ate_fim != null && (
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${r.dias_ate_fim <= (b.rule?.dias_antes_fim || 0) ? "bg-red-50 text-red-700 border-red-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>
                            {r.dias_ate_fim}d
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
